import { Button } from '@/components/ui/button';
import { useRegistrationStore } from '@/lib/store/registrationStore';
import { useQuery } from '@tanstack/react-query';
import { getMarketplaceApps } from '@/lib/api/controlPlaneClient';
import { cn } from '@/lib/utils';
import { Check, Loader2 } from 'lucide-react';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';

interface StepAppInterestsProps {
  onSubmit: () => void;
  isSubmitting: boolean;
}

export function StepAppInterests({ onSubmit, isSubmitting }: StepAppInterestsProps) {
  const { formData, updateFormData, error } = useRegistrationStore();
  const t = usePageTranslation({
    en: authTranslations,
    th: thAuthTranslations,
  });

  const { data: availableApps = [], isLoading } = useQuery({
    queryKey: ['registration', 'marketplaceApps'],
    queryFn: getMarketplaceApps,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
  });

  const handleToggle = (appName: string) => {
    const currentInterests = formData.appInterests;
    if (currentInterests.includes(appName)) {
      // Remove
      updateFormData({
        appInterests: currentInterests.filter((a) => a !== appName),
      });
    } else {
      // Add
      updateFormData({
        appInterests: [...currentInterests, appName],
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground md:text-center">
          {t.whichAppsToTry}
        </h2>
      </div>

      <div className="space-y-3">
        {isLoading ? (
          <div className="text-center py-4 text-muted-foreground">{t.loadingApps}</div>
        ) : (
          availableApps.map((app) => {
            const isSelected = formData.appInterests.includes(app.name);
            return (
              <button
                key={app.name}
                type="button"
                onClick={() => handleToggle(app.name)}
                disabled={isSubmitting}
                className={cn(
                  'w-full flex items-center gap-4 p-4 rounded-md border transition-all',
                  isSelected
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-muted',
                  isSubmitting && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span className="text-2xl shrink-0">{app.icon}</span>
                <div className="flex-1 text-left">
                  <div className="font-medium text-foreground">{app.app_name}</div>
                  <div className="text-sm text-muted-foreground md:text-center">{app.description}</div>
                </div>
                <div
                  className={cn(
                    'w-6 h-6 rounded-xs border-2 flex items-center justify-center shrink-0 transition-colors',
                    isSelected
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/30'
                  )}
                >
                  {isSelected && <Check className="w-4 h-4 text-white" />}
                </div>
              </button>
            );
          })
        )}
      </div>

      {error && (
        <div className="text-sm text-destructive text-center bg-destructive/10 p-3 rounded-md">
          {error}
        </div>
      )}

      <Button onClick={onSubmit} className="w-full h-12 rounded-md text-base" size="lg" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t.creatingWorkspace}
          </>
        ) : (
          t.startFreeTrial
        )}
      </Button>
    </div>
  );
}
